import { useDisclosure } from '@chakra-ui/react';
import { useState } from 'react';

import { DateRange } from '../types';
import { useCalendarControl } from './use-calendar-control';
import { useDateRangeControl } from './use-date-range-control';

type DatePickerOptions = {
	onChange: (value: DateRange) => void;
	value: DateRange;
};

export const useDateRangePicker = ({ onChange, value }: DatePickerOptions) => {
	const popover = useDisclosure();
	const calendar = useCalendarControl({ initialDate: value[0] });

	// We store the interim date in state instead of directly calling the onChange handler.
	// This requires the user to click "Apply" to confirm the date selection.
	const [interimValue, setInterimValue] = useState(value);

	const range = useDateRangeControl({
		value: interimValue,
		onChange: setInterimValue,
	});

	const onApply = () => {
		popover.onClose();
		onChange(interimValue);
	};

	const onOpen = () => {
		popover.onOpen();
		calendar.onResetCalendar();
		setInterimValue(value);
	};

	const setRange = (newRange: DateRange) => {
		setInterimValue(newRange);
		// Keep the range control's internal state machine consistent so a
		// subsequent calendar click extends the typed range instead of starting over.
		range.seedRange(newRange);
		const [, end] = newRange;

		if (end) {
			calendar.onGoToMonth(end);
		}
	};

	return {
		interimValue,
		isOpen: popover.isOpen,
		month: calendar.month,
		onApply,
		onClear: () => onChange([null, null]),
		onClose: popover.onClose,
		onGotoToday: () => void 0,
		onHoverDate: range.onHoverDate,
		onNextMonth: calendar.onNextMonth,
		onOpen,
		onPrevMonth: calendar.onPrevMonth,
		onSelectDate: range.onSelectDate,
		selectionEnd: range.selectionEnd,
		selectionStart: range.selectionStart,
		setRange,
	};
};
